// =============================================================================
// FILE: packages/renderer/src/components/ErrorBoundary.tsx
// PURPOSE: App-wide error boundary. Catches render-time exceptions (incl. stale
//          lazy-chunk load failures). NO-ERROR POLICY: never shows the word
//          "Error" — presents a professional RCD²-branded re-calibration screen.
//          Stale dynamic-import failures auto-recover with a single reload.
// =============================================================================

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';
import { LogoMark } from './branding/LogoMark.js';

interface Props {
  readonly children: ReactNode;
}

interface State {
  readonly crashed: boolean;
}

const RELOAD_FLAG = '__r2_recalibrate_reloaded';

function isChunkLoadError(error: Error): boolean {
  const msg = `${error?.message ?? ''} ${error?.name ?? ''}`;
  return /dynamically imported module|Loading chunk|Importing a module script failed|Failed to fetch/i.test(msg);
}

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { crashed: false };

  static getDerivedStateFromError(): State {
    return { crashed: true };
  }

  override componentDidMount(): void {
    // App mounted cleanly — clear the one-shot reload guard for future recoveries.
    const t = setTimeout(() => {
      try { sessionStorage.removeItem(RELOAD_FLAG); } catch { /* ignore */ }
    }, 8000);
    // best-effort; no cleanup needed for a top-level boundary
    void t;
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Transparently self-heal a stale build (new deploy invalidated lazy chunks).
    if (isChunkLoadError(error)) {
      try {
        if (!sessionStorage.getItem(RELOAD_FLAG)) {
          sessionStorage.setItem(RELOAD_FLAG, '1');
          window.location.reload();
          return;
        }
      } catch { /* ignore storage issues */ }
    }
    // eslint-disable-next-line no-console
    console.warn('[Recalibration] render exception captured', info.componentStack);
  }

  private resetView = (): void => {
    try { sessionStorage.removeItem(RELOAD_FLAG); } catch { /* ignore */ }
    window.location.reload();
  };

  override render(): ReactNode {
    if (!this.state.crashed) return this.props.children;
    return (
      <div
        data-testid="error-boundary"
        className="min-h-screen w-full flex items-center justify-center p-8"
        style={{ background: 'radial-gradient(ellipse at top, #0e1b2e 0%, #0a1220 55%, #070b12 100%)' }}
      >
        <div
          className="w-full max-w-md text-center rounded-2xl px-8 py-10"
          style={{
            background: 'rgba(13,31,60,0.55)',
            border: '1px solid rgba(45,212,191,0.22)',
            boxShadow: '0 0 40px rgba(20,184,166,0.08)',
            backdropFilter: 'blur(12px)',
          }}
        >
          <div
            className="mx-auto h-16 w-16 rounded-2xl grid place-items-center mb-5"
            style={{ background: 'rgba(20,184,166,0.10)', border: '1px solid rgba(20,184,166,0.30)' }}
          >
            <LogoMark size={38} className="text-teal-400" />
          </div>

          <div className="text-[10px] uppercase tracking-[0.28em] mb-2" style={{ color: 'rgba(94,234,212,0.75)' }}>
            RCD² Command Station
          </div>
          <h2 className="text-xl font-semibold" style={{ color: '#eaf3ff' }}>
            System Re-Calibration in Progress
          </h2>
          <p className="mt-3 text-[13px] leading-relaxed" style={{ color: 'rgba(203,220,244,0.62)' }}>
            We're optimizing the intelligence engine. Please click below to refresh the Command Station.
          </p>

          <button
            type="button"
            data-testid="error-boundary-reset"
            onClick={this.resetView}
            className="mt-7 inline-flex items-center gap-2 px-6 h-11 rounded-full font-semibold text-[13px] transition-colors"
            style={{ background: '#14b8a6', color: '#04110f' }}
          >
            <RefreshCw className="h-4 w-4" />
            Reset View
          </button>
        </div>
      </div>
    );
  }
}
